// 收藏夹

import { defineStore } from 'pinia';
import { StoreModuleName } from '../../constants';
import { CustomFetchContext } from '~/src/api/requests/types';
import { fetchCollectionList, removeCollection } from '~/src/api/modules/user-center/collection';
import { UserCollectionListItem } from '~/src/api/modules/user-center/collection/types';
import { getOptionValue, getQuestionType } from '../exam-answer/utils';
import { QuestionTypeEnum } from '~/src/constants/exam/answerFlow';
import {
  UserCenterCollectionPageStatus,
  ComputedUserCollectionListItem,
  UserCollectTotalDetails,
} from './types';
import { useEnvironmentStore } from '../environment/index';
import { useUserCenterStore } from '../user-center';
import { CollectionType } from '~/src/constants/userCenter/index';
import { PlatformEnum } from '../environment/types';

export const useUserCenterCollectionStore = defineStore(StoreModuleName.USER_COLLECTION, () => {
  const envStore = useEnvironmentStore();
  const userCenterStore = useUserCenterStore();
  const rawData = ref<UserCollectionListItem[]>([]);
  // app里面需要总数--不能清空,不然每次是2遍请求
  const userCollectTotal = ref<UserCollectTotalDetails>({
    allTotal: 9999,
    todayTotal: 9999,
  });

  const getInitState = () => {
    return {
      userCollectionList: [], // 错题列表
      collectionTypeList: [
        // 收藏类型筛选
        {
          label: '全部收藏',
          value: CollectionType.AllFavorites,
        },
        {
          label: '今日收藏',
          value: CollectionType.TodayFavorites,
        },
      ],
      today: CollectionType.AllFavorites, // 当前收藏类型筛选
      startUpdateTime: '',
      endUpdateTime: '',
      selectIndexList: [], // 选择题目的index集合->(index不行，分页就出错)id 集合
      showSelectAllButton: false, // 全选
      deleteOperation: true,
      pageInfo: {
        // 分页相关
        page: 1,
        pageSize: 10,
        total: 0,
        hasNextPage: true,
      },
      isLoginStatus: true, // 微信小程序判断是否登录
    };
  };

  const userCenterCollectionPageStatus = reactive<UserCenterCollectionPageStatus>(getInitState());

  const resetState = () => {
    Object.assign(userCenterCollectionPageStatus, getInitState());
  };

  // 获取全部收藏和今日收藏的total,需要请求2次获取结果
  const getCollectTotal = async (today: string, fetchContext: CustomFetchContext) => {
    const { page, pageSize } = userCenterCollectionPageStatus.pageInfo;
    const { startUpdateTime } = userCenterCollectionPageStatus;
    const { subjectId: userSubject } = userCenterStore.state;
    const endUpdateTime = '';

    const res = await fetchCollectionList(
      {
        today,
        userSubject,
        pageSize,
        page,
        endUpdateTime,
        startUpdateTime,
      },
      { isSkipStatusCheck: envStore.platform === PlatformEnum.MINI_PROGRAM ? true : false },
      fetchContext
    );
    const { total } = res.data.pagination;

    if (today === CollectionType.AllFavorites) {
      // 全部收藏
      userCollectTotal.value.allTotal = total;
    } else if (today === CollectionType.TodayFavorites) {
      // 今日收藏
      userCollectTotal.value.todayTotal = total;
    }
  };

  const updateUserCollectionList = async (fetchContext: CustomFetchContext, isAdd?: boolean) => {
    const { page, pageSize } = userCenterCollectionPageStatus.pageInfo;
    const { today, startUpdateTime } = userCenterCollectionPageStatus;
    const { subjectId: userSubject } = userCenterStore.state;

    let endUpdateTime;

    if (
      userCenterCollectionPageStatus.endUpdateTime !== '' &&
      userCenterCollectionPageStatus.endUpdateTime !== null &&
      userCenterCollectionPageStatus.endUpdateTime !== undefined
    ) {
      endUpdateTime = (
        Number(userCenterCollectionPageStatus.endUpdateTime) +
        3600 * 24 -
        1
      ).toString();
    } else {
      endUpdateTime = '';
    }

    const res = await fetchCollectionList(
      {
        today,
        userSubject,
        pageSize,
        page,
        endUpdateTime,
        startUpdateTime,
      },
      { isSkipStatusCheck: envStore.platform === PlatformEnum.MINI_PROGRAM ? true : false },
      fetchContext
    );

    if (Number(res.status) === -101) {
      // 未登录
      userCenterCollectionPageStatus.isLoginStatus = false;
      userCenterCollectionPageStatus.pageInfo.hasNextPage = false; // van-list 暂停

      return false;
    }

    rawData.value = res.data.lists;
    const { total, hasNextPage } = res.data.pagination;

    // app 需要一进入页面就获取总数
    if (envStore.platform === PlatformEnum.APP) {
      if (userCollectTotal.value.allTotal === 9999) {
        // 获取的全部错题的结果
        getCollectTotal(CollectionType.TodayFavorites, fetchContext);
      } else if (userCollectTotal.value.todayTotal === 9999) {
        // 获取高频错题的结果
        getCollectTotal(CollectionType.AllFavorites, fetchContext);
      }

      // 防止特殊清情况,其他地方答题有记录,这里点击没有(实时刷新)(只会在第一次进入的页面的时候更新total)
      if (today === CollectionType.AllFavorites) {
        userCollectTotal.value.allTotal = total;
      } else if (today === CollectionType.TodayFavorites) {
        userCollectTotal.value.todayTotal = total;
      }
    }

    userCenterCollectionPageStatus.pageInfo.total = total;
    userCenterCollectionPageStatus.pageInfo.hasNextPage = hasNextPage;

    if (isAdd) {
      userCenterCollectionPageStatus.userCollectionList = [
        ...userCenterCollectionPageStatus.userCollectionList,
        ...computeUserCollectionList(res.data.lists),
      ];
    } else {
      userCenterCollectionPageStatus.userCollectionList = computeUserCollectionList(res.data.lists);
    }

    return res;
  };

  const computeUserCollectionList = (
    userCollectionList: UserCollectionListItem[]
  ): ComputedUserCollectionListItem[] => {
    return userCollectionList.map((item, index) => {
      return {
        id: item.questionId,
        serialNumber:
          userCenterCollectionPageStatus.pageInfo.pageSize *
            (userCenterCollectionPageStatus.pageInfo.page - 1) +
          index +
          1,
        analyze: item.analyze,
        options: isString(item.option)
          ? []
          : item.option?.map((cItem, index) => {
              return {
                label: cItem,
                value: getOptionValue(index),
              };
            }),
        questionType: item.questionType,
        answerType: item.answerType,
        computedQuestionType: getQuestionType(+item.questionType, +item.answerType), // 处理type
        questTypeName: item.showTypeName || QuestionTypeEnum[+item.questionType],
        questionTitle: item.questionTitle,
        sortSon: +item.sortSon,
        materialText: item.materialText,
        updateTime: item.updateTime,
      };
    });
  };

  // 选择
  const selectDeleteItem = ({ id, type }: { id: string; type: number }) => {
    // if (type) {
    //   userCenterCollectionPageStatus.selectIndexList.push(index);
    // } else {
    //   userCenterCollectionPageStatus.selectIndexList =
    //     userCenterCollectionPageStatus.selectIndexList.filter(item => item !== index);
    // }
    if (type === 1) {
      const index = userCenterCollectionPageStatus.selectIndexList.indexOf(id);

      if (index !== -1) {
        userCenterCollectionPageStatus.selectIndexList.splice(index, 1);
      }
    } else {
      if (!userCenterCollectionPageStatus.selectIndexList.includes(id)) {
        userCenterCollectionPageStatus.selectIndexList.push(id);
      }
    }
  };

  // 删除取消
  const updateCorrectionDeleteCancel = () => {
    userCenterCollectionPageStatus.selectIndexList = [];
  };

  // 全选/取消全选
  const updateCorrectionDeleteSelectAll = (type: number) => {
    // if (type) {
    //   const list: number[] = [];

    //   for (let i = 0; i < userCenterCollectionPageStatus.userCollectionList.length; i++) {
    //     list.push(i);
    //   }

    //   userCenterCollectionPageStatus.selectIndexList = list;
    // } else {
    //   userCenterCollectionPageStatus.selectIndexList = [];
    // }

    if (type === 1) {
      userCenterCollectionPageStatus.userCollectionList.forEach(item => {
        const index = userCenterCollectionPageStatus.selectIndexList.indexOf(item.id);

        if (index !== -1) {
          userCenterCollectionPageStatus.selectIndexList.splice(index, 1);
        }
      });
    } else {
      userCenterCollectionPageStatus.userCollectionList.forEach(item => {
        if (!userCenterCollectionPageStatus.selectIndexList.includes(item.id)) {
          userCenterCollectionPageStatus.selectIndexList.push(item.id);
        }
      });
    }
  };

  // 删除错题
  const deleteSelectCollectionQuestion = (
    option: { deleteAll?: boolean },
    fetchContext: CustomFetchContext
  ) => {
    if (option.deleteAll) {
      if (userCenterCollectionPageStatus.userCollectionList.length === 0) {
        return ElMessage.error('列表暂无数据');
      }

      ElMessageBox.confirm('确定删除所有题目吗？删除后数据将无法恢复', '温馨提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        await removeCollection({ isAll: 1 });
        ElMessage.success('删除成功');
        userCenterCollectionPageStatus.pageInfo.page = 1;
        userCenterCollectionPageStatus.selectIndexList = [];
        updateUserCollectionList(fetchContext);
        userCenterCollectionPageStatus.deleteOperation = true;
      });
    } else {
      if (userCenterCollectionPageStatus.selectIndexList.length === 0) {
        return ElMessage.error('请先选择题目后再删除');
      }

      // const qid: string[] = [];

      // userCenterCollectionPageStatus.userCollectionList.forEach((item, index: number) => {
      //   if (userCenterCollectionPageStatus.selectIndexList.includes(index)) {
      //     qid.push(item.id);
      //   }
      // });

      ElMessageBox.confirm('确定删除已选题目吗？删除后数据将无法恢复', '温馨提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        await removeCollection({ qid: userCenterCollectionPageStatus.selectIndexList });
        ElMessage.success('删除成功');
        userCenterCollectionPageStatus.pageInfo.page = 1;
        userCenterCollectionPageStatus.selectIndexList = [];
        updateUserCollectionList(fetchContext);
        userCenterCollectionPageStatus.deleteOperation = true;
      });
    }
  };

  // 更改筛选类型
  const updateCollectionType = (today: string, fetchContext: CustomFetchContext) => {
    if (userCenterCollectionPageStatus.today === today) {
      return;
    }

    userCenterCollectionPageStatus.pageInfo.page = 1;
    userCenterCollectionPageStatus.today = today;

    // 移动端切换类型由无限滚动组件加载数据
    if (!envStore.isMobile) {
      updateUserCollectionList(fetchContext);
    }
  };

  // 全选的监听
  const showSelectAllButtonComputed = computed(() => {
    return (
      userCenterCollectionPageStatus.userCollectionList.length > 0 &&
      userCenterCollectionPageStatus.userCollectionList.every(item =>
        userCenterCollectionPageStatus.selectIndexList.includes(item.id)
      )
    );
  });

  // 搜索search
  const updateCollectionSearch = (fetchContext: CustomFetchContext) => {
    userCenterCollectionPageStatus.pageInfo.page = 1;
    updateUserCollectionList(fetchContext);
  };

  const updateCollectionDeleteStatus = (deleteStatus: boolean) => {
    userCenterCollectionPageStatus.deleteOperation = deleteStatus;
  };

  // 取消批量
  const updateCollectionDeleteCancel = (deleteStatus: boolean) => {
    updateCorrectionDeleteCancel();
    updateCollectionDeleteStatus(true);
  };

  watch(
    () => showSelectAllButtonComputed.value,
    newValue => {
      userCenterCollectionPageStatus.showSelectAllButton = newValue;
    }
  );

  return {
    userCenterCollectionPageStatus,
    updateUserCollectionList,
    deleteSelectCollectionQuestion,
    updateCollectionType,
    updateCorrectionDeleteSelectAll,
    selectDeleteItem,
    updateCorrectionDeleteCancel,
    resetState,
    userCollectTotal,
    updateCollectionSearch,
    updateCollectionDeleteStatus,
    updateCollectionDeleteCancel,
  };
});
